import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { toast } from 'sonner';
import { Palette, Save, ArrowLeft, Moon, Sun, Monitor, Upload } from 'lucide-react';
import useLandingConfig from '../hooks/useLandingConfig';

export const BrandingSettings = () => {const { t } = useTranslation("landing");
  const { config, loading, updateConfig } = useLandingConfig();
  const [formData, setFormData] = useState({
    site_name: '',
    tagline: '',
    logo_url: '',
    logo_dark_url: '',
    favicon_url: '',
    primary_color: '#3B82F6',
    secondary_color: '#10B981',
    accent_color: '#8B5CF6',
    default_theme: 'dark',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_image_url: '',
    google_analytics_id: '',
    gtm_id: '',
    contact_email: '',
    contact_phone: '',
    contact_address: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config) {
      setFormData({
        site_name: config.site_name || '',
        tagline: config.tagline || '',
        logo_url: config.logo_url || '',
        logo_dark_url: config.logo_dark_url || '',
        favicon_url: config.favicon_url || '',
        primary_color: config.primary_color || '#3B82F6',
        secondary_color: config.secondary_color || '#10B981',
        accent_color: config.accent_color || '#8B5CF6',
        default_theme: config.default_theme || 'dark',
        meta_title: config.meta_title || '',
        meta_description: config.meta_description || '',
        meta_keywords: config.meta_keywords || '',
        og_image_url: config.og_image_url || '',
        google_analytics_id: config.google_analytics_id || '',
        gtm_id: config.gtm_id || '',
        contact_email: config.contact_email || '',
        contact_phone: config.contact_phone || '',
        contact_address: config.contact_address || ''
      });
    }
  }, [config]);

  const handleSave = async () => {
    setSaving(true);
    const result = await updateConfig(formData);
    setSaving(false);

    if (result.success) {
      toast.success('Branding settings saved!');
    } else {
      toast.error(result.error || 'Failed to save settings');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>);

  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <a href="/admin/landing">
              <ArrowLeft className="w-5 h-5" />
            </a>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Palette className="w-6 h-6 text-purple-500" />{t("brandingSettings", "Branding Settings")}

            </h1>
            <p className="text-slate-400">{t("customizeYourLanding", "Customize your landing page appearance")}</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-branding-btn">
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Site Identity */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">{t("siteIdentity", "Site Identity")}</CardTitle>
            <CardDescription className="text-slate-400">{t("basicSiteInformation", "Basic site information")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">{t("siteName", "Site Name")}</Label>
              <Input
                value={formData.site_name}
                onChange={(e) => setFormData({ ...formData, site_name: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="EzERP"
                data-testid="site-name-input" />
              
            </div>
            <div className="space-y-2">
              <Label className="text-white">{t("tagline", "Tagline")}</Label>
              <Input
                value={formData.tagline}
                onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="Enterprise Resource Planning Made Simple" />
              
            </div>
          </CardContent>
        </Card>

        {/* Theme & Colors */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">{t("themeColors", "Theme & Colors")}</CardTitle>
            <CardDescription className="text-slate-400">{t("visualAppearanceSettings", "Visual appearance settings")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">{t("defaultTheme", "Default Theme")}</Label>
              <Select value={formData.default_theme} onValueChange={(v) => setFormData({ ...formData, default_theme: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="dark" className="text-white">
                    <div className="flex items-center gap-2">
                      <Moon className="w-4 h-4" />{t("dark", "Dark")}
                    </div>
                  </SelectItem>
                  <SelectItem value="light" className="text-white">
                    <div className="flex items-center gap-2">
                      <Sun className="w-4 h-4" />{t("light", "Light")}
                    </div>
                  </SelectItem>
                  <SelectItem value="system" className="text-white">
                    <div className="flex items-center gap-2">
                      <Monitor className="w-4 h-4" />{t("system", "System")}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-white">{t("primary", "Primary")}</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    className="w-12 h-10 p-1 bg-slate-700 border-slate-600" />
                  
                  <Input
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white text-xs" />
                  
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-white">{t("secondary", "Secondary")}</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={formData.secondary_color}
                    onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                    className="w-12 h-10 p-1 bg-slate-700 border-slate-600" />
                  
                  <Input
                    value={formData.secondary_color}
                    onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white text-xs" />
                  
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-white">{t("accent", "Accent")}</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={formData.accent_color}
                    onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                    className="w-12 h-10 p-1 bg-slate-700 border-slate-600" />
                  
                  <Input
                    value={formData.accent_color}
                    onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white text-xs" />
                  
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logos */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">{t("logosImages", "Logos & Images")}</CardTitle>
            <CardDescription className="text-slate-400">{t("brandImagery", "Brand imagery")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">{t("logoUrlLight", "Logo URL (Light Theme)")}</Label>
              <Input
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="https://..." />
              
            </div>
            <div className="space-y-2">
              <Label className="text-white">{t("logoUrlDark", "Logo URL (Dark Theme)")}</Label>
              <Input
                value={formData.logo_dark_url}
                onChange={(e) => setFormData({ ...formData, logo_dark_url: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="https://..." />
              
            </div>
            <div className="space-y-2">
              <Label className="text-white">{t("faviconUrl", "Favicon URL")}</Label>
              <Input
                value={formData.favicon_url}
                onChange={(e) => setFormData({ ...formData, favicon_url: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="https://..." />
              
            </div>
          </CardContent>
        </Card>

        {/* SEO */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">{t("seoSettings", "SEO Settings")}</CardTitle>
            <CardDescription className="text-slate-400">{t("searchEngineOptimization", "Search engine optimization")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">{t("metaTitle", "Meta Title")}</Label>
              <Input
                value={formData.meta_title}
                onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="EzERP - Enterprise Resource Planning" />
              
            </div>
            <div className="space-y-2">
              <Label className="text-white">{t("metaDescription", "Meta Description")}</Label>
              <Textarea
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="Comprehensive ERP solution for modern businesses..."
                rows={3} />
              
            </div>
            <div className="space-y-2">
              <Label className="text-white">{t("metaKeywords", "Meta Keywords")}</Label>
              <Input
                value={formData.meta_keywords}
                onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="ERP, WMS, logistics, inventory" />
              
            </div>
            <div className="space-y-2">
              <Label className="text-white">{t("ogImageUrl", "OG Image URL")}</Label>
              <Input
                value={formData.og_image_url}
                onChange={(e) => setFormData({ ...formData, og_image_url: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="https://..." />
              
            </div>
          </CardContent>
        </Card>

        {/* Analytics */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">{t("analytics", "Analytics")}</CardTitle>
            <CardDescription className="text-slate-400">{t("trackingAndAnalytics", "Tracking and analytics")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">{t("googleAnalyticsId", "Google Analytics ID")}</Label>
              <Input
                value={formData.google_analytics_id}
                onChange={(e) => setFormData({ ...formData, google_analytics_id: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="G-XXXXXXXXXX" />
              
            </div>
            <div className="space-y-2">
              <Label className="text-white">{t("googleTagManager", "Google Tag Manager ID")}</Label>
              <Input
                value={formData.gtm_id}
                onChange={(e) => setFormData({ ...formData, gtm_id: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="GTM-XXXXXXX" />
              
            </div>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">{t("contactInformation", "Contact Information")}</CardTitle>
            <CardDescription className="text-slate-400">{t("displayedOnThe", "Displayed on the landing page")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">{t("contactEmail", "Contact Email")}</Label>
              <Input
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="contact@company.com" />
              
            </div>
            <div className="space-y-2">
              <Label className="text-white">{t("contactPhone", "Contact Phone")}</Label>
              <Input
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="+1 (555) 000-0000" />
              
            </div>
            <div className="space-y-2">
              <Label className="text-white">{t("address", "Address")}</Label>
              <Textarea
                value={formData.contact_address}
                onChange={(e) => setFormData({ ...formData, contact_address: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="123 Business St, City, Country"
                rows={2} />
              
            </div>
          </CardContent>
        </Card>
      </div>
    </div>);

};

export default BrandingSettings;